import { FC } from 'react';

import {
  daysUntilDeliveryDate,
  deliveryDateRelativeLabel,
  formatDeliveryDateShort,
  toDeliveryDateKey,
} from '@service/giftStore/utils/giftDeliveryDate.util';

/** 받는 날까지 남은 일수별 색 — 오늘·내일만 빨강, 나머지는 차분한 회색 */
export const desiredDeliveryDateTone = (date: string, now: Date = new Date()): string => {
  const days = daysUntilDeliveryDate(date, now);
  if (days < 0) return 'bg-gray-200 text-gray-600';
  return days <= 1 ? 'bg-rose-600 text-white' : 'bg-gray-100 text-gray-800';
};

const subTagClassName =
  'inline-flex shrink-0 items-center whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-bold';

/**
 * 구매자가 고른 배송 희망일 배지 (날짜 지정 브랜드 주문에만 값이 있다).
 * 출고 담당자가 날짜를 놓치지 않게 '받는 날 10/2(금) · 내일'처럼 남은 일수를 함께 보여 준다.
 * - done=true(배송완료·취소)면 긴급도 색 없이 날짜만.
 * - late=true면 결제(입금)가 기한을 넘겨 받는 날이 자동 변경된 건 — date는 이미 바뀐 날짜이고,
 *   requested(처음 고른 날짜)가 있으면 '입금 지연 · 10/2(금)에서 변경'으로 어디서 바뀌었는지 보여 준다.
 * - edited=true면 배송관리에서 손으로 바꾼 건 — '수정됨' 표시.
 * - prominent 모드는 날짜를 직접 눌러 수정하고 남은 일수는 아래에 표시한다.
 * - onEdit을 넘기면 기본 모드에서는 [✎ 수정] 버튼이 붙는다. 날짜가 없는 건은 canSet일 때만 [+ 받는 날 지정]을 그린다
 *   (날짜도 없고 지정 대상도 아니면 아무것도 그리지 않는다).
 */
export const DesiredDeliveryDateBadge: FC<{
  date?: string | Date | null;
  done?: boolean;
  late?: boolean;
  requested?: string | Date | null;
  edited?: boolean;
  onEdit?: () => void;
  canSet?: boolean;
  className?: string;
  prominent?: boolean;
}> = ({
  date,
  done = false,
  late = false,
  requested,
  edited = false,
  onEdit,
  canSet = false,
  className = '',
  prominent = false,
}) => {
  const key = toDeliveryDateKey(date);
  const editable = !!onEdit && !done;
  if (!key) {
    if (!editable || !canSet) return null;
    return (
      <button
        type="button"
        onClick={onEdit}
        className={`inline-flex shrink-0 items-center whitespace-nowrap rounded border border-dashed border-amber-400 bg-white px-1.5 py-0.5 text-[11px] font-bold text-amber-700 hover:bg-amber-50 ${className}`}
      >
        + 받는 날 지정
      </button>
    );
  }
  const requestedLabel = formatDeliveryDateShort(requested);
  // 자동 변경됐다가 손으로 처음 날짜로 되돌린 건은 "○에서 변경"이 틀린 말이 된다 — 표시하지 않는다
  const showLate = late && !done && toDeliveryDateKey(requested) !== key;
  const tone = done ? 'bg-gray-100 text-gray-600' : desiredDeliveryDateTone(key);
  const editedBadge =
    edited && !done ? (
      <span
        title={
          prominent
            ? '배송관리에서 받는 날을 수정한 건입니다. 날짜를 누르면 변경 이력이 보입니다.'
            : '배송관리에서 받는 날을 수정한 건입니다. 수정 버튼을 누르면 변경 이력이 보입니다.'
        }
        className={`${subTagClassName} border-indigo-200 bg-indigo-50 text-indigo-700`}
      >
        수정됨
      </span>
    ) : null;
  const compactDate = (
    <>
      <span
        className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-base font-bold ${tone}`}
      >
        {formatDeliveryDateShort(key)}
      </span>
      {!done && (
        <span className="mt-1 flex items-center gap-1 px-1.5 text-xs font-normal text-gray-500">
          <span className="whitespace-nowrap">{deliveryDateRelativeLabel(key)}</span>
          {editedBadge}
        </span>
      )}
    </>
  );
  const detailTagClass = prominent
    ? 'inline-block max-w-full whitespace-normal rounded border px-1.5 py-0.5 text-[11px] font-bold'
    : subTagClassName;
  return (
    <span
      className={`${
        prominent
          ? 'inline-flex w-24 flex-col items-start gap-1'
          : 'inline-flex flex-wrap items-center gap-1'
      } ${className}`}
    >
      {prominent ? (
        editable ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`받는 날 ${formatDeliveryDateShort(key)} 수정`}
            title="날짜를 눌러 받는 날 수정"
            className="rounded text-left underline decoration-dotted underline-offset-4 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            {compactDate}
          </button>
        ) : (
          <span>{compactDate}</span>
        )
      ) : (
        <span
          className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-bold ${tone}`}
        >
          받는 날 {formatDeliveryDateShort(key)}
          {!done && (
            <span className="opacity-90">· {deliveryDateRelativeLabel(key)}</span>
          )}
        </span>
      )}
      {showLate && (
        <span
          title="결제(입금)가 기한을 넘겨 받는 날이 가장 빠른 배송 가능일로 자동 변경되었습니다. 표시된 받는 날에 맞춰 출고하세요."
          className={`${detailTagClass} border-rose-300 bg-rose-50 text-rose-700`}
        >
          입금 지연{requestedLabel ? ` · ${requestedLabel}에서 변경` : ' · 날짜 변경됨'}
        </span>
      )}
      {!prominent && editedBadge}
      {editable && !prominent && (
        <button
          type="button"
          onClick={onEdit}
          title="받는 날 수정"
          className={`${subTagClassName} border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50`}
        >
          ✎ 수정
        </button>
      )}
    </span>
  );
};
